<template>
  <section id="sobre-mim" class="about-section">
    <div class="section-title">
      <h2 class="title">{{ $t('sections.about') }}</h2>
    </div>

    <div class="container">
      <p class="section-subtitle">{{ $t('about.subtitleIntro') }}</p>

      <p>
        <i18n-t keypath="about.intro1" tag="span">
          <template #name><strong>Danilo</strong></template>
          <template #role>
            <strong>{{ pick(profile.i18n).profession }}</strong>
          </template>
          <template #designDigital>
            <strong>{{ $t('about.highlights.designDigital') }}</strong>
          </template>
          <template #webFlash>
            <strong>{{ $t('about.highlights.webFlash') }}</strong>
          </template>
        </i18n-t>
      </p>

      <p>
        <i18n-t keypath="about.intro2" tag="span">
          <template #softwareFocus>
            <strong>{{ $t('about.highlights.softwareFocus') }}</strong>
          </template>
          <template #products>
            <strong>{{ $t('about.highlights.products') }}</strong>
          </template>
          <template #telecom>
            <strong>{{ $t('about.highlights.telecom') }}</strong>
          </template>
          <template #marketplaces>
            <strong>{{ $t('about.highlights.marketplaces') }}</strong>
          </template>
          <template #institutions>
            <strong>{{ $t('about.highlights.institutions') }}</strong>
          </template>
        </i18n-t>
      </p>

      <p>
        <i18n-t keypath="about.intro3" tag="span">
          <template #unicef><strong>UNICEF</strong></template>
          <template #porto><strong>Porto Seguro</strong></template>
          <template #tenda><strong>Tenda</strong></template>
          <template #ifood><strong>iFood</strong></template>
          <template #claro><strong>Claro</strong></template>
          <template #technicalDecisions>
            <strong>{{ $t('about.highlights.technicalDecisions') }}</strong>
          </template>
          <template #performance>
            <strong>{{ $t('about.highlights.performance') }}</strong>
          </template>
          <template #automatedTests>
            <strong>{{ $t('about.highlights.automatedTests') }}</strong>
          </template>
          <template #pipelineQuality>
            <strong>{{ $t('about.highlights.pipelineQuality') }}</strong>
          </template>
        </i18n-t>
      </p>

      <p class="section-subtitle mt">{{ $t('about.subtitleJourney') }}</p>

      <p
        v-for="(item, index) in journeyItems"
        :key="index"
      >
        {{ item }}
      </p>

      <p class="section-subtitle mt">{{ $t('about.subtitleWorkflow') }}</p>

      <p>
        <i18n-t keypath="about.workflow" tag="span">
          <template #performanceDetail>
            <strong>{{ $t('about.highlights.performanceDetail') }}</strong>
          </template>
          <template #quality>
            <strong>{{ $t('about.highlights.quality') }}</strong>
          </template>
          <template #coverage>
            <strong>{{ $t('about.highlights.coverage') }}</strong>
          </template>
          <template #qualityGate>
            <strong>{{ $t('about.highlights.qualityGate') }}</strong>
          </template>
          <template #codeReview>
            <strong>{{ $t('about.highlights.codeReview') }}</strong>
          </template>
          <template #harnessEngineering>
            <strong>{{ $t('about.highlights.harnessEngineering') }}</strong>
          </template>
          <template #sdd>
            <strong>{{ $t('about.highlights.sdd') }}</strong>
          </template>
          <template #cursor>
            <strong>{{ $t('about.highlights.cursor') }}</strong>
          </template>
        </i18n-t>
      </p>

      <div class="sr-only" :aria-label="$t('about.sr.stackHeading')">
        <h2>{{ $t('about.sr.stackHeading') }}</h2>
        <template v-for="(items, key) in skillKeywords" :key="key">
          <h3>{{ $t(`about.sr.categories.${key}`) }}</h3>
          <ul>
            <li v-for="kw in items" :key="kw">{{ kw }}</li>
          </ul>
        </template>
      </div>

      <div class="sr-only" :aria-label="$t('about.sr.coursesHeading')">
        <h2>{{ $t('about.sr.coursesHeading') }}</h2>
        <ul>
          <li v-for="course in courses" :key="course.id">
            {{ pick(course.i18n).title }} · {{ pick(course.i18n).institution }}
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalized } from '@/composables/useLocalized';
import { profile } from '@/data/profile';
import { courses } from '@/data/courses';
import { skillKeywords } from '@/data/seo';

const { pick } = useLocalized();
const { tm } = useI18n();

const journeyItems = computed(() => tm('about.journey.items') as string[]);
</script>

<style scoped lang="scss">
.about-section {
  scroll-margin-top: 80px;
}
</style>
